/* eslint-disable object-curly-newline */
import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Checkbox,
  Icon,
  TooltipHost,
  TooltipOverflowMode,
  DirectionalHint,
} from '@bingads-webui-react/fabric-control';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import {
  FolderIcon,
  TextWithHighlight,
  TilesGridCellCard,
  TilesGridCellDecorativeLabel as CellLabel,
} from '@bingads-webui-campaign-react/asset-library-components';
import { CustomFluentUIIcons } from '@bingads-webui-campaign-react/asset-library-resources';

import { getTimeDescription, FolderAssetType } from '@bingads-webui-campaign-react/asset-library-model';

import {
  iconStyles,
  videoSvgIconStyles,
  getFolderGridCellStyle
} from './styles/folders-grid-cell-style';

function getCountDescription({
  itemInfo,
  i18n,
}) {
  let subFoldersCountDesc = '';
  let imagesCountDesc = '';
  let videosCountDesc = '';
  if (itemInfo.subfoldersCount === 1) {
    subFoldersCountDesc = i18n.getString(_TL_('{{count}} folder'), { count: itemInfo.subfoldersCount });
  } else if (!_.isUndefined(itemInfo.subfoldersCount)) {
    subFoldersCountDesc = i18n.getString(_TL_('{{count}} folders'), { count: itemInfo.subfoldersCount });
  }
  if (itemInfo.imageAssetsCount === 1) {
    imagesCountDesc = i18n.getString(_TL_('{{count}} image'), { count: itemInfo.imageAssetsCount });
  } else if (!_.isUndefined(itemInfo.imageAssetsCount)) {
    imagesCountDesc = i18n.getString(_TL_('{{count}} images'), { count: itemInfo.imageAssetsCount });
  }
  if (itemInfo.videoAssetsCount === 1) {
    videosCountDesc = i18n.getString(_TL_('{{count}} video'), { count: itemInfo.videoAssetsCount });
  } else if (!_.isUndefined(itemInfo.videoAssetsCount)) {
    videosCountDesc = i18n.getString(_TL_('{{count}} videos'), { count: itemInfo.videoAssetsCount });
  }
  const res = [subFoldersCountDesc, imagesCountDesc, videosCountDesc].filter(item => item !== '').join(', ');
  return res;
}

const convertItemInfo = ({ allowedAssetType, itemInfos }) => {
  const result = {
    subfoldersCount: itemInfos.SubFoldersCount,
  };
  if (allowedAssetType === FolderAssetType.Image) {
    result.imageAssetsCount = itemInfos.ImageAssetsCount;
  } else if (allowedAssetType === FolderAssetType.Video) {
    result.videoAssetsCount = itemInfos.VideoAssetsCount;
  } else if (!allowedAssetType) {
    result.imageAssetsCount = itemInfos.ImageAssetsCount;
    result.videoAssetsCount = itemInfos.VideoAssetsCount;
  }

  return result;
};

// eslint-disable-next-line react/prop-types
const FolderCountInfo = ({ itemInfo, className, i18n, isEmptyFolder }) => (
  <TooltipHost
    overflowMode={TooltipOverflowMode.Self}
    hostClassName={classnames('folder-cell-tooltip', className)}
    content={getCountDescription({ itemInfo, i18n })}
    directionalHint={DirectionalHint.bottomLeftEdge}
  >
    { !isEmptyFolder ? (
      <React.Fragment>
        {!_.isUndefined(itemInfo.subfoldersCount) &&
          <React.Fragment>
            <Icon iconName="FabricFolder" styles={iconStyles} />
            <span className="cell-subfolder-count">{itemInfo.subfoldersCount}</span>
          </React.Fragment>
        }
        {!_.isUndefined(itemInfo.imageAssetsCount) &&
          <React.Fragment>
            <Icon iconName="Photo2" styles={iconStyles} />
            <span className="cell-image-count">{itemInfo.imageAssetsCount}</span>
          </React.Fragment>
        }
        {!_.isUndefined(itemInfo.videoAssetsCount) &&
          <React.Fragment>
            <Icon iconName={CustomFluentUIIcons.SquareVideoSVGIcon} styles={videoSvgIconStyles} />
            <span className="cell-video-count">{itemInfo.videoAssetsCount}</span>
          </React.Fragment>
        }
      </React.Fragment>
      ) : (
        <span className="empty-desc">{i18n.getString(_TL_('0 items'))}</span>
    )}
  </TooltipHost>
);

const getFolderThumbnail = ({ assets }) => {
  const firstAsset = _.first(assets) || {};

  // MT will ensure valid assets returned first, so UI only need to check first asset.
  if (firstAsset.folderAssetType === FolderAssetType.Video) {
    return firstAsset.status === 'Active' ? firstAsset.thumbnailUrl : '';
  }

  return firstAsset.url;
};

const FoldersGridCell = ({
  item,
  highlightedText,
  onFolderClick,
  i18n,
  selectable,
  selected,
  onSelect,
  classes,
  allowedAssetType,

  renderAccessory,
}) => {
  //
  const {
    folderName, itemCount, assets, createdTime,
  } = item || {};

  const itemInfo = useMemo(() => convertItemInfo({ allowedAssetType, itemInfos: itemCount }), [allowedAssetType, itemCount]);
  const isEmptyFolder = !_.isUndefined(itemCount.TotalCount) && itemCount.TotalCount === 0;

  const timeDesc = getTimeDescription(createdTime, i18n);
  const thumbnailUrl = useMemo(() => getFolderThumbnail({ assets }), [assets]);

  const onClick = useCallback(() => {
    onFolderClick(item);
  }, [item, onFolderClick]);

  const onCheckboxChange = useCallback((event, checked) => {
    if (onSelect) {
      onSelect(checked);
    }
  }, [onSelect]);

  const hasAccessory = renderAccessory || selectable;

  return (
    <div className={classnames('tiles-grid-cell-content', classes.folderGridCellContainer)}>
      <TilesGridCellCard customCellCardClass={selectable ? classes.selectableCellCard : ''}>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={classnames('folder-grid-cell-content', hasAccessory && 'grid-cell-with-accessory')}
          onClick={onClick}
          data-is-focusable
        >
          <div className="folder-thumbnail">
            <FolderIcon thumbnailImageUrl={thumbnailUrl} isEmptyFolder={isEmptyFolder} />
          </div>
          <div className="folder-info">
            <div className="folder-name">
              <TextWithHighlight text={folderName || ''} highlightedText={highlightedText} enableTooltip />
            </div>
            <div className="folder-newlabel-counting">
              {timeDesc &&
                <CellLabel
                  classes={{ cellLabel: classes.folderCellNewLabel }}
                  text={i18n.getString(_TL_('New'))}
                  secondaryText={timeDesc}
                  iconName="AlertSolid"
                />
              }
              <FolderCountInfo
                itemInfo={itemInfo}
                className="counting-info"
                i18n={i18n}
                isEmptyFolder={isEmptyFolder}
              />
            </div>
          </div>
        </div>
        {hasAccessory ?
          <div className="folder-cell-accessory">
            {selectable &&
            <Checkbox onChange={onCheckboxChange} checked={selected} ariaLabel={folderName || i18n.getString(_TL_('Folder'))} />
            }
            {(renderAccessory && !selectable) && renderAccessory()}
          </div>
        : null}
      </TilesGridCellCard>
    </div>
  );
};

FoldersGridCell.propTypes = {
  item: PropTypes.shape({}).isRequired,
  highlightedText: PropTypes.string,
  onFolderClick: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,

  // show checkbox
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,

  // injected styles
  classes: PropTypes.shape({
    folderGridCellContainer: PropTypes.string,
    folderCellNewLabel: PropTypes.string,
    selectableCellCard: PropTypes.string,
    folderCellContextMenuButton: PropTypes.string,
  }).isRequired,
  allowedAssetType: PropTypes.string,

  renderAccessory: PropTypes.func,
};

FoldersGridCell.defaultProps = {
  highlightedText: undefined,
  selectable: false,
  selected: false,
  onSelect: undefined,
  allowedAssetType: null,

  renderAccessory: undefined,
};

export const AssetLibraryFoldersGridCell
  = withDefaultStyles(FoldersGridCell, getFolderGridCellStyle);
